import { SwUpdate } from '@angular/service-worker';

/**
 * Recarrega a aplicação garantindo que o build que volta é o publicado, e não o que já estava
 * guardado.
 *
 * <p>Um `location.reload()` sozinho não basta, e é por isso que o aviso de atualização voltava
 * logo depois de o usuário aceitar recarregar: o reload comum honra os caches, então o documento
 * pode vir do cache HTTP do navegador e, com o service worker ativo, a navegação inteira é
 * atendida por ele. O usuário só saía do laço com Ctrl+Shift+R, que é justamente o que ignora os
 * dois.</p>
 *
 * <p>O `activateUpdate` sozinho também não resolve: ele só troca alguma coisa quando o worker já
 * baixou uma versão nova. Quando quem detectou o atraso foi o backend — pelo cabeçalho de versão —,
 * o worker pode não ter descoberto nada ainda, e aí não há o que ativar.</p>
 *
 * <p>Daí os quatro passos abaixo, do mais suave ao mais drástico. Cada um pode falhar sem impedir
 * os seguintes: o reload no fim é o que importa, e é melhor recarregar de forma imperfeita do que
 * deixar o usuário preso num aviso que não sai da tela.</p>
 */
export async function recarregarComVersaoNova(updates: SwUpdate): Promise<void> {
  await ativarBuildBaixado(updates);
  await descartarCachesDoWorker();
  await desregistrarWorkers();
  await revalidarDocumento();
  document.location.reload();
}

/** 1) O caminho feliz: há um build novo já baixado, e ativá-lo basta. */
async function ativarBuildBaixado(updates: SwUpdate): Promise<void> {
  if (!updates.isEnabled) {
    return;
  }
  try {
    await updates.activateUpdate();
  } catch {
    // Sem versão nova em cache não há o que ativar — os passos seguintes cuidam do resto.
  }
}

/**
 * 2) Apaga o que o service worker guardou. É aqui que mora o app shell antigo: sem esta limpeza,
 * a próxima navegação continuaria sendo servida do cache do worker.
 */
async function descartarCachesDoWorker(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }
  try {
    const nomes = await caches.keys();
    await Promise.all(nomes.map(nome => caches.delete(nome)));
  } catch {
    // CacheStorage indisponível (contexto inseguro, modo privado): segue sem limpar.
  }
}

/**
 * 3) Tira o worker do caminho da próxima navegação. Ele volta a se registrar assim que a
 * aplicação nova sobe — o registro faz parte do bootstrap.
 */
async function desregistrarWorkers(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
    return;
  }
  try {
    const registros = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registros.map(registro => registro.unregister()));
  } catch {
    // Sem suporte ou sem permissão: o reload ainda acontece.
  }
}

/**
 * 4) Renova a cópia do documento no cache HTTP do navegador.
 *
 * <p>`cache: 'reload'` faz a requisição ignorar o que está guardado e **gravar** a resposta nova no
 * lugar — é o que o `location.reload()` seguinte vai encontrar. Sem isso, um `index.html` que
 * chegou com cache longo continuaria apontando para os bundles da versão anterior, e todo o resto
 * teria sido em vão.</p>
 */
async function revalidarDocumento(): Promise<void> {
  try {
    await fetch(document.location.href, { cache: 'reload', credentials: 'same-origin' });
  } catch {
    // Offline ou bloqueado: o reload tenta de novo pela via normal.
  }
}
